/**
 * Nold Go — Mes alertes de trajets (recherches sauvegardées).
 * Permet au passager de sauvegarder une recherche et d'être notifié push + email
 * dès qu'un trajet correspondant est publié.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell, X, Loader2, ArrowRight, MapPin, Plus } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import SEOHead from "../components/SEOHead";

const WEEKDAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

export default function CarpoolAlerts() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const [cities, setCities] = useState([]);
  const [from, setFrom] = useState("Bruxelles");
  const [to, setTo] = useState("Paris");
  const [weekday, setWeekday] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/carpool/saved-searches").then(r => setItems(r.data.items || [])).catch(() => setItems([]));
  useEffect(() => {
    if (!user) { nav("/login"); return; }
    load();
    api.get("/carpool/cities").then(r => setCities(r.data.cities || [])).catch(() => {});
  }, [user]);

  const create = async () => {
    if (from === to) { toast.error("Départ ≠ arrivée"); return; }
    setBusy(true);
    try {
      await api.post("/carpool/saved-searches", {
        from_city: from, to_city: to,
        weekday: weekday === "" ? null : Number(weekday),
      });
      toast.success("Alerte créée — vous serez notifié dès qu'un trajet correspond");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette alerte ?")) return;
    try {
      await api.delete(`/carpool/saved-searches/${id}`);
      toast.success("Alerte supprimée");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  if (!items) return <div className="min-h-screen flex items-center justify-center bg-[#FDF8F0]"><Loader2 className="w-6 h-6 animate-spin text-[#75807A]" /></div>;

  return (
    <div className="min-h-screen bg-[#FDF8F0]" data-testid="carpool-alerts-page">
      <SEOHead title="Mes alertes covoiturage — Nold Go" description="Recevez une notification dès qu'un trajet correspondant est publié." canonical="/covoiturage/mes-alertes" noindex />
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-11 h-11 rounded-2xl bg-[#F26419] flex items-center justify-center"><Bell className="w-6 h-6 text-white" /></div>
          <h1 className="font-head text-3xl font-black text-[#1A1A1A]">Mes alertes</h1>
        </div>

        {/* Créer alerte */}
        <div className="bg-white rounded-2xl p-4 border border-[#E2DFD8] mb-4">
          <div className="text-xs font-bold text-[#75807A] uppercase tracking-widest mb-2">Nouvelle alerte</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <select data-testid="alert-from" value={from} onChange={(e) => setFrom(e.target.value)}
              className="h-11 rounded-xl border border-[#E2DFD8] px-3 text-sm">
              {cities.map((c) => <option key={c}>{c}</option>)}
            </select>
            <select data-testid="alert-to" value={to} onChange={(e) => setTo(e.target.value)}
              className="h-11 rounded-xl border border-[#E2DFD8] px-3 text-sm">
              {cities.map((c) => <option key={c}>{c}</option>)}
            </select>
            <select data-testid="alert-weekday" value={weekday} onChange={(e) => setWeekday(e.target.value)}
              className="h-11 rounded-xl border border-[#E2DFD8] px-3 text-sm">
              <option value="">Tous les jours</option>
              {WEEKDAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
            </select>
          </div>
          <button data-testid="alert-create" onClick={create} disabled={busy}
            className="mt-3 w-full h-11 rounded-xl bg-[#F26419] hover:bg-[#D85312] text-white font-bold text-sm disabled:opacity-50 inline-flex items-center justify-center gap-1">
            <Plus className="w-4 h-4" /> Créer l'alerte
          </button>
        </div>

        {/* Liste alertes */}
        {items.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-[#E2DFD8] p-8 text-center text-[#75807A]">
            Aucune alerte pour l'instant. Créez-en une ci-dessus.
          </div>
        ) : items.map((s) => (
          <div key={s.id} data-testid={`alert-${s.id}`} className="bg-white rounded-2xl p-4 border border-[#E2DFD8] mb-2 flex items-center justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="font-bold inline-flex items-center gap-1 flex-wrap">
                <MapPin className="w-4 h-4 text-[#F26419]" /> {s.from_city} <ArrowRight className="w-4 h-4 text-[#75807A]" /> {s.to_city}
              </div>
              <div className="text-xs text-[#75807A] mt-1">
                Jour : <b className="text-[#1A1A1A]">{s.weekday === null || s.weekday === undefined ? "Tous les jours" : WEEKDAYS[s.weekday]}</b>
                {s.notified_trip_ids?.length > 0 && <span> · {s.notified_trip_ids.length} trajet(s) déjà notifié(s)</span>}
              </div>
            </div>
            <button data-testid={`alert-delete-${s.id}`} onClick={() => remove(s.id)}
              className="p-2 rounded-full text-[#DC2626] hover:bg-[#DC2626]/10">
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}

        <p className="mt-6 text-center text-xs text-[#75807A]">
          <Link to="/covoiturage" className="underline">← Retour à la recherche</Link>
        </p>
      </div>
    </div>
  );
}
